// features/procurement/components/VendorBulkUploadModal.tsx
/**
 * Vendor Bulk Upload Hub — Enterprise Modal
 * Drag-and-drop spreadsheet uploader, template download, column reference modal,
 * and live row-by-row validation report.
 */

import { useState, useRef } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '@/store/authStore';
import { useToastStore } from '@/store/toastStore';
import {
    downloadVendorTemplate,
    bulkUploadVendors,
    VendorBulkUploadResult,
} from '../services/procurementApi';
import {
    Upload, Download, FileSpreadsheet, CheckCircle,
    AlertTriangle, X, CloudUpload, Table2, Sparkles,
    ShieldCheck, Truck, RefreshCw, FileText
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { clsx } from 'clsx';

interface VendorBulkUploadModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSuccess?: () => void;
}

interface ColumnGuide {
    col: string;
    name: string;
    required: boolean;
    example: string;
    desc: string;
}

const VENDOR_TEMPLATE_COLUMNS: ColumnGuide[] = [
    { col: 'A', name: 'Vendor Name', required: true, example: 'Dangote Flour Mills', desc: 'Company or supplier name. Must be unique.' },
    { col: 'B', name: 'Contact Person', required: false, example: 'Alhaji Musa Sani', desc: 'Primary contact representative.' },
    { col: 'C', name: 'Email', required: false, example: 'sales@dangotemills.com', desc: 'Official business email.' },
    { col: 'D', name: 'Phone', required: false, example: '08031234567', desc: 'Primary phone number.' },
    { col: 'E', name: 'Alternate Phone', required: false, example: '08099887766', desc: 'Secondary backup phone.' },
    { col: 'F', name: 'Website', required: false, example: 'https://dangote.com', desc: 'Company website URL.' },
    { col: 'G', name: 'Address', required: false, example: '12 Wharf Road, Apapa', desc: 'Physical office/warehouse address.' },
    { col: 'H', name: 'City', required: false, example: 'Lagos', desc: 'City or municipality.' },
    { col: 'I', name: 'State', required: false, example: 'Lagos', desc: 'State or region.' },
    { col: 'J', name: 'Category', required: false, example: 'Food & Beverages', desc: 'Industry supply category.' },
    { col: 'K', name: 'Payment Terms', required: false, example: 'Net 30', desc: 'Payment terms (e.g. Net 30, Cash on Delivery).' },
    { col: 'L', name: 'Tax ID', required: false, example: 'TIN-98765432-0001', desc: 'Tax Identification Number (TIN / VAT).' },
    { col: 'M', name: 'Bank Name', required: false, example: 'Zenith Bank', desc: 'Bank for settlement payments.' },
    { col: 'N', name: 'Account Number', required: false, example: '1012345678', desc: '10-digit NUBAN account number.' },
    { col: 'O', name: 'Account Name', required: false, example: 'Dangote Flour Mills Plc', desc: 'Bank account holder name.' },
    { col: 'P', name: 'Notes', required: false, example: 'Preferred bulk flour vendor', desc: 'Procurement remarks or terms.' },
];

export function VendorBulkUploadModal({ isOpen, onClose, onSuccess }: VendorBulkUploadModalProps) {
    const user = useAuthStore((s) => s.user);
    const tenantId = user?.tenantId || '';
    const addToast = useToastStore((s) => s.addToast);
    const queryClient = useQueryClient();

    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [isDragging, setIsDragging] = useState(false);
    const [uploadResult, setUploadResult] = useState<VendorBulkUploadResult | null>(null);
    const [showGuideModal, setShowGuideModal] = useState(false);
    const [isDownloading, setIsDownloading] = useState(false);

    const fileInputRef = useRef<HTMLInputElement>(null);

    // Mutation for Bulk Upload
    const uploadMutation = useMutation({
        mutationFn: (file: File) => bulkUploadVendors(tenantId, file),
        onSuccess: (data) => {
            setUploadResult(data);
            queryClient.invalidateQueries({ queryKey: ['vendors', tenantId] });
            if (data.successCount > 0) {
                addToast({
                    type: 'success',
                    title: 'Vendors Uploaded Successfully',
                    message: `Imported ${data.successCount} vendor${data.successCount !== 1 ? 's' : ''} to your directory.`,
                });
                onSuccess?.();
            }
        },
        onError: (error: any) => {
            addToast({
                type: 'error',
                title: 'Upload Failed',
                message: error?.response?.data?.error?.message || error?.message || 'Could not process vendor spreadsheet.',
                duration: 6000,
            });
        },
    });

    if (!isOpen) return null;

    const handleFileSelect = (file: File) => {
        if (!file.name.match(/\.(xlsx|xls|csv)$/i)) {
            addToast({
                type: 'error',
                title: 'Unsupported File Format',
                message: 'Please upload an Excel spreadsheet (.xlsx) or CSV file.',
            });
            return;
        }
        setSelectedFile(file);
        setUploadResult(null);
    };

    const handleDownloadTemplate = async () => {
        setIsDownloading(true);
        try {
            const blob = await downloadVendorTemplate(tenantId);
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = 'etipos-vendor-template.xlsx';
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
            addToast({
                type: 'success',
                title: 'Template Downloaded',
                message: 'Official vendor Excel template downloaded successfully.',
            });
        } catch (error: any) {
            addToast({
                type: 'error',
                title: 'Download Failed',
                message: error?.message || 'Could not download vendor template.',
            });
        } finally {
            setIsDownloading(false);
        }
    };

    const handleLoadSampleData = () => {
        const sampleCsv = `Vendor Name,Contact Person,Email,Phone,Alternate Phone,Website,Address,City,State,Category,Payment Terms,Tax ID,Bank Name,Account Number,Account Name,Notes
"Dangote Flour Mills Plc","Alhaji Musa Sani","sales@dangote.com","08031234567","08099887766","https://dangote.com","12 Wharf Road","Apapa","Lagos","Food & Beverages","Net 30","TIN-100234-01","Zenith Bank","1012345678","Dangote Flour Mills Plc","Key supplier for baking flour"
"Flour Mills of Nigeria Plc","Chinedu Okafor","orders@fmnplc.com","08022334455","","https://fmnplc.com","1 Golden Penny Drive","Apapa","Lagos","Food & Beverages","Net 30","TIN-200456-02","First Bank","2034567890","Flour Mills of Nigeria Plc","Golden penny semo & pasta"
"Chi Limited (TGI Group)","Amina Bello","supply@chilimited.com","08055667788","","https://chilimited.com","14 Chivita Avenue","Ijaodola","Lagos","Beverages","Cash on Delivery","TIN-300678-03","Access Bank","0011223344","Chi Limited","Juice and dairy drinks supplier"`;

        const blob = new Blob([sampleCsv], { type: 'text/csv' });
        const file = new File([blob], 'sample-vendors-catalog.csv', { type: 'text/csv' });
        handleFileSelect(file);
        addToast({
            type: 'info',
            title: 'Sample Vendors Loaded',
            message: 'Click "Start Bulk Upload" to process the sample supplier records.',
        });
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-xs">
            <motion.div
                initial={{ scale: 0.95, opacity: 0, y: 15 }}
                animate={{ scale: 1, opacity: 1, y: 0 }}
                exit={{ scale: 0.95, opacity: 0, y: 15 }}
                className="bg-white rounded-3xl shadow-2xl border border-slate-200 w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col"
            >
                {/* Header */}
                <div className="px-6 py-5 border-b border-slate-100 flex items-center justify-between bg-gradient-to-r from-teal-50 to-slate-50">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-2xl bg-teal-600 text-white flex items-center justify-center shadow-md shadow-teal-500/20">
                            <Truck className="w-5 h-5" />
                        </div>
                        <div>
                            <h2 className="text-lg font-black text-slate-900 flex items-center gap-2">
                                Bulk Upload Vendors & Suppliers
                                <span className="text-[10px] uppercase font-bold tracking-wider px-2 py-0.5 rounded-full bg-teal-100 text-teal-700">
                                    Excel / CSV
                                </span>
                            </h2>
                            <p className="text-xs text-slate-500">
                                Add your supplier directory in bulk using the official Excel template.
                            </p>
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-2 rounded-xl text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors"
                    >
                        <X size={20} />
                    </button>
                </div>

                {/* Body Content */}
                <div className="p-6 overflow-y-auto space-y-5 flex-1">
                    {/* Top Guide & Download Strip */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
                        <div className="p-4 bg-slate-50 rounded-2xl border border-slate-200/80 flex flex-col justify-between">
                            <div>
                                <div className="flex items-center gap-2 text-xs font-bold text-teal-700 mb-1">
                                    <Sparkles className="w-3.5 h-3.5" /> 1. Get Official Template
                                </div>
                                <p className="text-xs text-slate-500 leading-relaxed mb-3">
                                    Pre-formatted with 16 vendor fields (Contact, TIN, Payment Terms, Bank Details) for zero-error import.
                                </p>
                            </div>
                            <button
                                onClick={handleDownloadTemplate}
                                disabled={isDownloading}
                                className="w-full flex items-center justify-center gap-2 px-3.5 py-2.5 bg-teal-600 hover:bg-teal-700 text-white font-bold rounded-xl text-xs transition-colors shadow-xs"
                            >
                                <Download size={14} />
                                {isDownloading ? 'Downloading...' : 'Download .XLSX Template'}
                            </button>
                        </div>

                        <div className="p-4 bg-slate-50 rounded-2xl border border-slate-200/80 flex flex-col justify-between">
                            <div>
                                <div className="flex items-center gap-2 text-xs font-bold text-slate-700 mb-1">
                                    <ShieldCheck className="w-3.5 h-3.5 text-teal-600" /> 2. Review Column Specs
                                </div>
                                <p className="text-xs text-slate-500 leading-relaxed mb-3">
                                    Duplicate vendor names are automatically skipped without overwriting existing supplier profiles.
                                </p>
                            </div>
                            <div className="flex items-center gap-2">
                                <button
                                    onClick={() => setShowGuideModal(true)}
                                    className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 bg-white hover:bg-slate-100 text-slate-700 border border-slate-200 font-bold rounded-xl text-xs transition-colors"
                                >
                                    <Table2 size={13} /> Column Reference
                                </button>
                                <button
                                    onClick={handleLoadSampleData}
                                    className="px-3 py-2 bg-slate-200 hover:bg-slate-300 text-slate-700 font-bold rounded-xl text-xs transition-colors"
                                    title="Load 3 sample Nigerian food & beverage suppliers"
                                >
                                    Try Sample
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* Drag & Drop Upload Area */}
                    <div
                        onDragOver={(e) => {
                            e.preventDefault();
                            setIsDragging(true);
                        }}
                        onDragLeave={() => setIsDragging(false)}
                        onDrop={(e) => {
                            e.preventDefault();
                            setIsDragging(false);
                            if (e.dataTransfer.files?.[0]) {
                                handleFileSelect(e.dataTransfer.files[0]);
                            }
                        }}
                        onClick={() => fileInputRef.current?.click()}
                        className={clsx(
                            'border-2 border-dashed rounded-3xl p-8 text-center cursor-pointer transition-all',
                            isDragging
                                ? 'border-teal-500 bg-teal-50/60 scale-[0.99]'
                                : selectedFile
                                ? 'border-teal-300 bg-teal-50/20'
                                : 'border-slate-300 hover:border-teal-400 bg-white hover:bg-slate-50/50'
                        )}
                    >
                        <input
                            ref={fileInputRef}
                            type="file"
                            accept=".xlsx,.xls,.csv"
                            className="hidden"
                            onChange={(e) => {
                                if (e.target.files?.[0]) handleFileSelect(e.target.files[0]);
                            }}
                        />

                        <div className="w-14 h-14 rounded-2xl bg-teal-100/80 text-teal-700 flex items-center justify-center mx-auto mb-3 shadow-inner">
                            {uploadMutation.isPending ? (
                                <RefreshCw className="w-7 h-7 animate-spin" />
                            ) : selectedFile ? (
                                <FileSpreadsheet className="w-7 h-7 text-teal-600" />
                            ) : (
                                <CloudUpload className="w-7 h-7" />
                            )}
                        </div>

                        {selectedFile ? (
                            <div>
                                <p className="text-sm font-black text-slate-900">{selectedFile.name}</p>
                                <p className="text-xs text-slate-500 mt-0.5">
                                    {(selectedFile.size / 1024).toFixed(1)} KB · Ready to import
                                </p>
                                <button
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setSelectedFile(null);
                                        setUploadResult(null);
                                    }}
                                    className="text-xs text-rose-500 font-bold hover:underline mt-2 inline-block"
                                >
                                    Choose different file
                                </button>
                            </div>
                        ) : (
                            <div>
                                <p className="text-sm font-bold text-slate-800">
                                    Drag and drop your vendor spreadsheet here, or <span className="text-teal-600">browse</span>
                                </p>
                                <p className="text-xs text-slate-400 mt-1">
                                    Supports Microsoft Excel (.xlsx, .xls) and CSV files
                                </p>
                            </div>
                        )}
                    </div>

                    {/* Upload Results View */}
                    {uploadResult && (
                        <div className="p-5 bg-slate-50 rounded-3xl border border-slate-200 space-y-4">
                            <div className="flex items-center justify-between">
                                <h3 className="text-sm font-black text-slate-900 flex items-center gap-2">
                                    {uploadResult.hasErrors ? (
                                        <AlertTriangle className="w-4 h-4 text-amber-500" />
                                    ) : (
                                        <CheckCircle className="w-4 h-4 text-emerald-600" />
                                    )}
                                    Import Summary
                                </h3>
                                <span className={clsx(
                                    'text-xs font-black px-2.5 py-1 rounded-full',
                                    uploadResult.hasErrors ? 'bg-amber-100 text-amber-800' : 'bg-emerald-100 text-emerald-800'
                                )}>
                                    {uploadResult.successCount} of {uploadResult.totalRows} Imported
                                </span>
                            </div>

                            {/* Metrics Strip */}
                            <div className="grid grid-cols-4 gap-2 text-center">
                                <div className="p-2.5 bg-white rounded-xl border border-slate-200">
                                    <div className="text-lg font-black text-slate-800">{uploadResult.totalRows}</div>
                                    <div className="text-[10px] uppercase font-bold text-slate-400">Total Rows</div>
                                </div>
                                <div className="p-2.5 bg-emerald-50 rounded-xl border border-emerald-200">
                                    <div className="text-lg font-black text-emerald-700">{uploadResult.successCount}</div>
                                    <div className="text-[10px] uppercase font-bold text-emerald-600">Imported</div>
                                </div>
                                <div className="p-2.5 bg-slate-100 rounded-xl border border-slate-200">
                                    <div className="text-lg font-black text-slate-600">{uploadResult.skippedCount}</div>
                                    <div className="text-[10px] uppercase font-bold text-slate-500">Skipped (Exists)</div>
                                </div>
                                <div className="p-2.5 bg-rose-50 rounded-xl border border-rose-200">
                                    <div className="text-lg font-black text-rose-700">{uploadResult.errorCount}</div>
                                    <div className="text-[10px] uppercase font-bold text-rose-600">Errors</div>
                                </div>
                            </div>

                            {/* Errors Table (if any) */}
                            {uploadResult.errors.length > 0 && (
                                <div className="mt-3 border border-rose-200 bg-white rounded-2xl overflow-hidden">
                                    <div className="px-4 py-2.5 bg-rose-50 border-b border-rose-100 text-xs font-bold text-rose-800">
                                        Row Issues ({uploadResult.errors.length})
                                    </div>
                                    <div className="max-h-40 overflow-y-auto divide-y divide-slate-100 text-xs">
                                        {uploadResult.errors.map((err, idx) => (
                                            <div key={idx} className="p-3 flex items-start justify-between gap-4">
                                                <div>
                                                    <span className="font-bold text-slate-900">Row {err.row}: {err.vendorName}</span>
                                                    <p className="text-rose-600 text-[11px] mt-0.5">{err.message}</p>
                                                </div>
                                                {err.suggestedFix && (
                                                    <span className="text-[10px] text-slate-400 bg-slate-100 px-2 py-1 rounded-lg flex-shrink-0">
                                                        {err.suggestedFix}
                                                    </span>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {/* Footer Controls */}
                <div className="px-6 py-4 border-t border-slate-100 flex items-center justify-between bg-slate-50">
                    <button
                        onClick={onClose}
                        className="px-4 py-2.5 rounded-xl border border-slate-200 text-slate-600 hover:bg-slate-100 font-bold text-xs transition-colors"
                    >
                        {uploadResult ? 'Done' : 'Cancel'}
                    </button>

                    <button
                        onClick={() => selectedFile && uploadMutation.mutate(selectedFile)}
                        disabled={!selectedFile || uploadMutation.isPending}
                        className={clsx(
                            'flex items-center gap-2 px-6 py-2.5 rounded-xl text-white font-bold text-xs transition-all shadow-md',
                            !selectedFile || uploadMutation.isPending
                                ? 'bg-slate-300 cursor-not-allowed shadow-none'
                                : 'bg-teal-600 hover:bg-teal-700 shadow-teal-500/25 active:scale-95'
                        )}
                    >
                        {uploadMutation.isPending ? (
                            <>
                                <RefreshCw className="w-4 h-4 animate-spin" />
                                Processing File...
                            </>
                        ) : (
                            <>
                                <Upload size={14} />
                                Start Bulk Upload
                            </>
                        )}
                    </button>
                </div>
            </motion.div>

            {/* Column Guide Reference Modal */}
            <AnimatePresence>
                {showGuideModal && (
                    <div
                        className="fixed inset-0 z-60 flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-xs"
                        onClick={() => setShowGuideModal(false)}
                    >
                        <motion.div
                            initial={{ scale: 0.95, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            exit={{ scale: 0.95, opacity: 0 }}
                            onClick={(e) => e.stopPropagation()}
                            className="bg-white rounded-3xl shadow-2xl border border-slate-200 w-full max-w-2xl max-h-[85vh] overflow-hidden flex flex-col"
                        >
                            <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between bg-slate-50">
                                <h3 className="font-bold text-slate-900 text-sm flex items-center gap-2">
                                    <Table2 className="w-4 h-4 text-teal-600" />
                                    Vendor Spreadsheet Column Reference
                                </h3>
                                <button
                                    onClick={() => setShowGuideModal(false)}
                                    className="p-1 rounded-lg text-slate-400 hover:bg-slate-100"
                                >
                                    <X size={18} />
                                </button>
                            </div>

                            <div className="p-6 overflow-y-auto space-y-3">
                                {VENDOR_TEMPLATE_COLUMNS.map((col) => (
                                    <div
                                        key={col.col}
                                        className="p-3 rounded-2xl border border-slate-100 bg-slate-50/50 flex items-start justify-between gap-3"
                                    >
                                        <div className="flex items-start gap-3">
                                            <div className="w-7 h-7 rounded-xl bg-teal-100 text-teal-700 font-bold flex items-center justify-center text-xs flex-shrink-0">
                                                {col.col}
                                            </div>
                                            <div>
                                                <div className="flex items-center gap-2">
                                                    <span className="font-bold text-slate-800 text-xs">{col.name}</span>
                                                    {col.required ? (
                                                        <span className="px-1.5 py-0.5 rounded text-[9px] font-black uppercase bg-rose-100 text-rose-700">
                                                            Required
                                                        </span>
                                                    ) : (
                                                        <span className="px-1.5 py-0.5 rounded text-[9px] font-bold uppercase bg-slate-100 text-slate-500">
                                                            Optional
                                                        </span>
                                                    )}
                                                </div>
                                                <p className="text-xs text-slate-500 mt-0.5">{col.desc}</p>
                                            </div>
                                        </div>
                                        <span className="text-[11px] font-mono text-slate-600 bg-white px-2 py-1 rounded-lg border border-slate-200/80 flex-shrink-0">
                                            e.g. {col.example}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        </motion.div>
                    </div>
                )}
            </AnimatePresence>
        </div>
    );
}
